import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { sendPortalEvent } from "@/lib/portalIngest";

const REVIEW_EMAIL = "alex@skillmammoth.com";
const CLIENT_EMAIL = "info@kodiakse.com";
const CLIENT_PHONE_E164 = "+16519831609";
const CLIENT_REPLY_TO = "info@kodiakse.com";
const FROM_EMAIL = "Kodiak Structures & Exteriors <support@notifications.skillmammoth.com>";
const FOOTER_HTML = `<p style="margin-top:32px;padding-top:16px;border-top:1px solid #eee;font-size:12px;color:#888;text-align:center;font-family:Arial,sans-serif">This lead brought to you by your <a href="https://skillmammoth.com" style="color:#888;text-decoration:underline">Skill Mammoth Digital Smart Website</a>.</p>`;

const SYSTEM_PROMPT = `You classify inbound web-form submissions for Kodiak Structures & Exteriors, a roofing, siding, windows, gutters, and storm-damage contractor serving the Twin Cities Metro (Minnesota).

A GENUINE lead is a homeowner, property owner, property manager, or anyone asking about roofing, siding, windows, gutters, storm/hail damage, insurance claims, inspections, repairs, or estimates — even if the message is short, vague, or has typos.

SPAM = someone trying to SELL something TO the business rather than hire it. Examples:
- SEO, marketing, web design, social media, PPC, or lead-gen agencies
- Backlink, guest post, or "collaboration" pitches
- Directory listings, "get more leads" offers
- Franchise, partnership, investment, M&A pitches
- Recruiters, staffing agencies
- Software / SaaS / supplier vendor outreach
- Crypto, forex, get-rich-quick
- Obvious bots (random characters, keyword stuffing, off-topic content)

Rules:
- Bias STRONGLY toward "genuine". If there's any reasonable chance it's a real customer, return "genuine".
- Use "unsure" only when it's ambiguous but leans suspicious.
- Use "spam" ONLY when it is clearly a solicitation, pitch, or bot.
- A short message like "roof leak" or "need estimate" is GENUINE.
- Return STRICT JSON: {"verdict":"genuine"|"spam"|"unsure","reason":"<one short sentence>"}`;

type Lead = Record<string, unknown>;

async function classify(lead: Lead): Promise<{ verdict: string; reason: string }> {
  const key = process.env['LOVABLE_API_KEY'];
  if (!key) return { verdict: "genuine", reason: "AI key missing — fail open" };

  const payload = {
    name: `${lead['first_name'] ?? ""} ${lead['last_name'] ?? ""}`.trim(),
    email: lead['email'], phone: lead['phone'], address: lead['address'],
    city: lead['city'], state: lead['state'], zip: lead['zip_code'],
    source: lead['source'], notes: lead['notes'],
  };

  try {
    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${key}` },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        response_format: { type: "json_object" },
        messages: [
          { role: "system", content: SYSTEM_PROMPT },
          { role: "user", content: `Classify this form submission:\n${JSON.stringify(payload, null, 2)}` },
        ],
      }),
    });
    if (!res.ok) {
      console.error("AI gateway error", res.status, await res.text());
      return { verdict: "genuine", reason: "AI error — fail open" };
    }
    const data = (await res.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    const content = data?.choices?.[0]?.message?.content ?? "{}";
    const parsed = JSON.parse(content) as { verdict?: string; reason?: unknown };
    const verdict = ["genuine", "spam", "unsure"].includes(parsed.verdict ?? "") ? parsed.verdict! : "genuine";
    return { verdict, reason: String(parsed.reason ?? "").slice(0, 500) };
  } catch (e) {
    console.error("Classify failed", e);
    return { verdict: "genuine", reason: "AI exception — fail open" };
  }
}

async function sendEmail(opts: { to: string | string[]; subject: string; html: string; replyTo?: string }) {
  const key = process.env['RESEND_API_KEY'];
  if (!key) { console.error("RESEND_API_KEY missing"); return; }
  const body: Record<string, unknown> = {
    from: FROM_EMAIL,
    to: Array.isArray(opts.to) ? opts.to : [opts.to],
    subject: opts.subject,
    html: opts.html,
  };
  if (opts.replyTo) body['reply_to'] = opts.replyTo;
  const res = await fetch("https://api.resend.com/emails", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${key}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) console.error("Resend error", res.status, await res.text());
}

async function sendSms(bodyText: string) {
  const lovableKey = process.env['LOVABLE_API_KEY'];
  const twilioKey = process.env['TWILIO_API_KEY'];
  const from = process.env['TWILIO_FROM_NUMBER'];
  if (!lovableKey || !twilioKey || !from) {
    console.error("Twilio env vars missing", { lovableKey: !!lovableKey, twilioKey: !!twilioKey, from: !!from });
    return;
  }
  const res = await fetch("https://connector-gateway.lovable.dev/twilio/Messages.json", {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${lovableKey}`,
      "X-Connection-Api-Key": twilioKey,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({ To: CLIENT_PHONE_E164, From: from, Body: bodyText.slice(0, 1500) }),
  });
  if (!res.ok) console.error("Twilio error", res.status, await res.text());
}

const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

function leadRowsHtml(lead: Lead) {
  const fields: [string, string][] = [
    ["Name", `${lead['first_name'] ?? ""} ${lead['last_name'] ?? ""}`.trim()],
    ["Email", ""], ["Phone", ""],
    ["Address", `${lead['address'] ?? ""}, ${lead['city'] ?? ""}, ${lead['state'] ?? ""} ${lead['zip_code'] ?? ""}`],
    ["Source", String(lead['source'] ?? "")],
    ["Notes", String(lead['notes'] ?? "") || "—"],
  ];
  const emailLink = lead['email'] ? `<a href="mailto:${esc(lead['email'])}">${esc(lead['email'])}</a>` : "—";
  const digits = String(lead['phone'] ?? "").replace(/[^\d+]/g, "");
  const phoneLink = lead['phone'] ? `<a href="tel:${esc(digits)}">${esc(lead['phone'])}</a>` : "—";
  return fields.map(([label, val]) => {
    let display = esc(val);
    if (label === "Email") display = emailLink;
    if (label === "Phone") display = phoneLink;
    return `<tr><td style="padding:6px 14px 6px 0;color:#555;vertical-align:top"><b>${label}</b></td><td style="padding:6px 0">${display}</td></tr>`;
  }).join("");
}

function businessNotifyHtml(lead: Lead, verdict: string, reason: string) {
  return `<div style="font-family:Arial,sans-serif;font-size:14px;color:#111;max-width:600px;margin:0 auto;padding:24px">
    <h2 style="color:#1A1A1A;margin:0 0 8px">🔔 New Lead — Kodiak Website</h2>
    <p style="margin:0 0 16px;color:#555">A new estimate request just came in.</p>
    ${verdict !== "genuine" ? `<p style="background:#FFF4E5;border-left:3px solid #E8730E;padding:10px 14px;font-size:13px"><b>AI review:</b> ${esc(verdict)} — ${esc(reason)}</p>` : ""}
    <table style="border-collapse:collapse;margin-top:8px">${leadRowsHtml(lead)}</table>
    ${FOOTER_HTML}
  </div>`;
}

function customerReplyHtml(lead: Lead) {
  const first = esc(lead['first_name'] ?? "there");
  return `<div style="font-family:Arial,sans-serif;font-size:15px;color:#111;max-width:600px;margin:0 auto;padding:24px;line-height:1.55">
    <h2 style="color:#1A1A1A;margin:0 0 12px">Thanks for reaching out, ${first}!</h2>
    <p>We received your request and a member of the Kodiak Structures &amp; Exteriors team will be in touch shortly — usually within one business day.</p>
    <p>If it's urgent (storm or active leak), give us a call anytime at <a href="tel:+16519831609" style="color:#E8730E;font-weight:bold">(651) 983-1609</a>.</p>
    <p style="margin-top:20px">In the meantime, feel free to reply to this email with photos or any extra details about your project — it helps us prepare an accurate estimate.</p>
    <p style="margin-top:28px">— The Kodiak Team<br/>
    <a href="mailto:info@kodiakse.com" style="color:#555">info@kodiakse.com</a> · <a href="tel:+16519831609" style="color:#555">(651) 983-1609</a></p>
    ${FOOTER_HTML}
  </div>`;
}

function reviewCopyHtml(lead: Lead, verdict: string, reason: string, blocked: boolean) {
  return `<div style="font-family:Arial,sans-serif;font-size:14px;color:#111;max-width:600px;margin:0 auto;padding:24px">
    <p style="background:${blocked ? "#FEE" : "#FFF4E5"};border-left:3px solid ${blocked ? "#B00" : "#E8730E"};padding:10px 14px">
      <b>AI verdict:</b> ${esc(verdict)}<br/><b>Reason:</b> ${esc(reason)}<br/>
      ${blocked ? "<b>This submission was BLOCKED and NOT sent to Kodiak.</b>" : "Borderline — also forwarded to Kodiak."}
    </p>
    <table style="border-collapse:collapse">${leadRowsHtml(lead)}</table>
    ${FOOTER_HTML}
  </div>`;
}

function smsBody(lead: Lead) {
  const name = `${lead['first_name'] ?? ""} ${lead['last_name'] ?? ""}`.trim();
  return `NEW KODIAK LEAD\n${name}\n${lead['phone'] ?? ""}\n${lead['email'] ?? ""}\n${lead['address'] ?? ""}, ${lead['city'] ?? ""} ${lead['zip_code'] ?? ""}\nSource: ${lead['source'] ?? ""}${lead['notes'] ? `\nNotes: ${String(lead['notes']).slice(0, 300)}` : ""}`;
}

async function notifyBusiness(lead: Lead, verdict: string, reason: string) {
  await Promise.allSettled([
    sendEmail({
      to: CLIENT_EMAIL,
      subject: `🔔 New Lead — ${lead['first_name'] ?? ""} ${lead['last_name'] ?? ""} (${lead['city'] ?? ""})`,
      html: businessNotifyHtml(lead, verdict, reason),
      replyTo: String(lead['email'] ?? CLIENT_REPLY_TO),
    }),
    lead['email'] ? sendEmail({
      to: String(lead['email']),
      subject: "We got your request — Kodiak Structures & Exteriors",
      html: customerReplyHtml(lead),
      replyTo: CLIENT_REPLY_TO,
    }) : Promise.resolve(),
    sendSms(smsBody(lead)),
  ]);
}

interface FilterLeadInput {
  lead_id: string;
}

export const filterLead = createServerFn({ method: "POST" })
  .inputValidator((input: unknown): FilterLeadInput => {
    const data = input as FilterLeadInput;
    if (!data || !data.lead_id) {
      throw new Error("lead_id required");
    }
    return data;
  })
  .handler(async ({ data }) => {
    const supabase = createClient(
      process.env['SUPABASE_URL']!,
      process.env['SUPABASE_SERVICE_ROLE_KEY']!,
    );

    const { data: lead, error } = await supabase.from("form_leads").select("*").eq("id", data.lead_id).maybeSingle();
    if (error || !lead) {
      throw new Error("lead not found");
    }

    const { verdict, reason } = await classify(lead);
    const is_spam = verdict === "spam";
    await supabase.from("form_leads").update({ is_spam, spam_reason: reason }).eq("id", data.lead_id);

    if (verdict === "spam") {
      await sendEmail({
        to: REVIEW_EMAIL,
        subject: "🚫 Solicitation blocked — Kodiak website",
        html: reviewCopyHtml(lead, verdict, reason, true),
      });
    } else if (verdict === "unsure") {
      await Promise.allSettled([
        sendEmail({
          to: REVIEW_EMAIL,
          subject: "Kodiak lead (review — borderline)",
          html: reviewCopyHtml(lead, verdict, reason, false),
        }),
        notifyBusiness(lead, verdict, reason),
      ]);
    } else {
      await notifyBusiness(lead, verdict, reason);
    }

    // Dual-write to the portal AFTER the lead is stored and the business has
    // been notified. Blocked solicitations are deliberately not forwarded —
    // the portal leads inbox should match what Kodiak actually gets emailed.
    if (!is_spam) {
      const name = `${lead['first_name'] ?? ""} ${lead['last_name'] ?? ""}`.trim();
      await sendPortalEvent(
        "lead.created",
        {
          name: name || null,
          email: (lead['email'] as string) ?? null,
          phone: (lead['phone'] as string) ?? null,
          message: (lead['notes'] as string) ?? null,
          source: (lead['source'] as string) ?? "website",
          address: lead['address'] ?? null,
          city: lead['city'] ?? null,
          state: lead['state'] ?? null,
          zip_code: lead['zip_code'] ?? null,
          spam_verdict: verdict,
          site_lead_id: lead['id'],
        },
        {
          dedupeKey: `lead-${lead['id']}`,
          occurredAt: (lead['created_at'] as string) ?? null,
        },
      );
    }

    return { verdict, reason };
  });
